import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AuthLayoutWrapper } from '@/components/auth/AuthLayoutWrapper';
import logoImage from '@/assets/prsnl-logo.svg';
import authIllustration from '@/assets/auth/auth-illustration.jpg';
import { validateCustomerEmail, signInWithOtp } from '@/services/data/auth';
import { supabase } from '@/integrations/supabase/client';

interface SignupScreenProps {
  onSignup: () => void;
  onNavigateToLogin?: () => void;
}

export function SignupScreen({ onSignup, onNavigateToLogin }: SignupScreenProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email) || email.length > 254) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsLoading(true);
    
    try {
      // First, check if user exists and needs reactivation
      const { data: reactivateData, error: reactivateError } = await supabase.functions.invoke('reactivate-user', {
        body: { 
          email: email.toLowerCase(),
          redirect_to: `${window.location.origin}/set-password`
        }
      });

      if (reactivateError) {
        console.error('Error checking reactivation:', reactivateError);
        setError('Une erreur est survenue. Veuillez réessayer.');
        setIsLoading(false);
        return;
      }

      // User was reactivated - password reset email sent
      if (reactivateData?.reactivated) {
        setSuccessMessage('Votre compte a été réactivé. Vérifiez votre boîte mail pour définir un nouveau mot de passe.');
        setIsLoading(false);
        return;
      }

      // User exists but hasn't completed signup - magic link sent
      if (reactivateData?.pending_signup) {
        setSuccessMessage('Vérifiez votre boîte mail pour finaliser votre inscription.');
        setIsLoading(false);
        return;
      }

      // User exists and has completed signup - redirect to login
      if (reactivateData?.exists && reactivateData?.has_signed_up) {
        setError('Vous avez déjà un compte actif. Utilisez la page de connexion.');
        setIsLoading(false);
        return;
      }

      // User doesn't exist - proceed with normal validation and signup
      const validationResult = await validateCustomerEmail(email);

      if (!validationResult.success) {
        console.error('Error validating email:', validationResult.error);
        setError('Une erreur est survenue. Veuillez réessayer.');
        setIsLoading(false);
        return;
      }

      if (!validationResult.data?.valid) {
        if (validationResult.data?.reason === 'already_signed_up') {
          setError('Vous avez déjà un compte. Utilisez la page de connexion.');
        } else {
          setError('Cette adresse email n\'est pas enregistrée. Veuillez contacter votre administrateur.');
        }
        setIsLoading(false);
        return;
      }

      // Send magic link
      const magicLinkResult = await signInWithOtp(email, window.location.origin);

      if (!magicLinkResult.success) {
        console.error('Magic link error:', magicLinkResult.error);
        setError(magicLinkResult.error?.message || 'Une erreur est survenue lors de l\'envoi du lien');
        setIsLoading(false);
        return;
      }

      // Redirect to login with success message
      onSignup();
    } catch (error) {
      console.error('Unexpected error during signup:', error);
      setError('Une erreur inattendue est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayoutWrapper illustrationSrc={authIllustration}>
      <div className="text-center">
        <img 
          src={logoImage} 
          alt="PRSNL" 
          className="w-40 h-auto mx-auto mb-12 brightness-0 invert"
        />
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 space-y-3">
              <p className="text-sm text-red-300">{error}</p>
              {(error.includes('déjà un compte') || error.includes('compte actif')) && onNavigateToLogin && (
                <Button
                  type="button"
                  onClick={onNavigateToLogin}
                  variant="outline"
                  className="w-full"
                >
                  Aller à la connexion
                </Button>
              )}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4">
              <p className="text-sm text-green-300">{successMessage}</p>
            </div>
          )}
          
          <div className="space-y-3 text-left">
            <label htmlFor="email" className="text-white text-sm block mb-2">Email</label>
            <Input
              id="email"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="bg-white/5 backdrop-blur-sm border-white/10 text-white rounded-xl h-12 px-5 text-base placeholder:text-white/30"
            />
            <p className="text-xs text-white/50 mt-2">
              Contactez l'équipe si vous ne savez pas avec quel email vous connecter.
            </p>
          </div>
          
          <Button
            type="submit"
            disabled={isLoading || !!successMessage}
            className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-medium transition-all"
          >
            {isLoading ? 'Envoi en cours...' : 'Recevoir le lien de connexion'}
          </Button>
        </form>

        {onNavigateToLogin && (
          <div className="mt-6">
            <button
              onClick={onNavigateToLogin}
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              Déjà un compte ? <span className="underline">Se connecter</span>
            </button>
          </div>
        )}
      </div>
    </AuthLayoutWrapper>
  );
}
